<template>
  <div class="page-shell user-page">
    <div class="topbar user-page__topbar">
      <div class="page-title user-page__title">
        <h1><span class="title-icon">👤</span>用户管理</h1>
        <p>管理平台注册用户与认证审核</p>
      </div>
    </div>

    <div class="card user-list-card">
      <div class="search-bar user-search-bar">
        <input
          v-model.trim="draftFilters.name"
          type="text"
          placeholder="搜索姓名/企业名"
          @keyup.enter="applySearch"
        >
        <select v-model="draftFilters.type">
          <option value="">全部类型</option>
          <option value="individual">个人</option>
          <option value="mcn">MCN机构</option>
        </select>
        <button class="btn btn-outline" type="button" @click="applySearch">
          <span>🔍</span>
          <span>查询</span>
        </button>
      </div>

      <div class="table-wrap user-table-wrap">
        <table>
          <thead>
            <tr>
              <th>ID</th>
              <th>用户类型</th>
              <th>姓名/企业名</th>
              <th>手机号</th>
              <th>附加信息</th>
              <th>注册时间</th>
              <th>认证状态</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="8" class="table-empty">加载中...</td>
            </tr>
            <tr v-else-if="!list.length">
              <td colspan="8" class="table-empty">暂无用户记录</td>
            </tr>
            <template v-else>
              <tr v-for="item in list" :key="item.id">
                <td>{{ item.id }}</td>
                <td>
                  <span :class="['tag', item.type === 'mcn' ? 'tag-purple' : 'tag-blue']">{{ item.typeText }}</span>
                </td>
                <td>{{ item.name }}</td>
                <td>{{ item.phone }}</td>
                <td>
                  <div class="extra-info">
                    <template v-if="item.type === 'individual'">
                      <div>身份证：{{ item.certification && item.certification.idCardNo ? item.certification.idCardNo : '—' }}</div>
                    </template>
                    <template v-else-if="item.type === 'mcn'">
                      <div>企业名称：{{ item.name || '—' }}</div>
                      <div>信用代码：{{ item.companyCode || '—' }}</div>
                    </template>
                    <template v-else>
                      <div>—</div>
                    </template>
                  </div>
                </td>
                <td>{{ item.createdAt }}</td>
                <td>
                  <span :class="statusClass(item.certificationStatus)">{{ item.certificationStatusText }}</span>
                </td>
                <td>
                  <button class="btn btn-sm btn-outline" type="button" @click="openUserDialog(item, 'detail')">查看</button>
                  <button
                    v-if="canReview(item)"
                    class="btn btn-sm btn-success ml-5"
                    type="button"
                    @click="openUserDialog(item, 'review')"
                  >
                    去审核
                  </button>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <div class="user-pagination">
        <el-pagination
          background
          layout="total, prev, pager, next, jumper"
          :current-page="pagination.currentPage"
          :page-size="pagination.pageSize"
          :total="pagination.total"
          @current-change="handlePageChange"
        />
      </div>
    </div>

    <el-dialog :title="dialogTitle" :visible.sync="userDialogVisible" width="920px">
      <div v-if="dialogLoading" class="dialog-state">加载中...</div>
      <div v-else-if="currentUser">
        <div class="detail-section">
          <h4>基本信息</h4>
          <div class="info-grid">
            <div
              v-for="field in userInfoFields"
              :key="field.label"
              :class="['info-item', field.wide ? 'full-width' : '']"
            >
              <span class="ii-label">{{ field.label }}</span>
              <span class="ii-value">{{ field.value }}</span>
            </div>
          </div>
        </div>

        <div class="detail-section">
          <h4>认证信息</h4>
          <div v-if="currentUser.certification" class="content-block certification-block">
            <div class="info-grid">
              <div
                v-for="field in certificationInfoFields"
                :key="field.label"
                :class="['info-item', field.wide ? 'full-width' : '']"
              >
                <span class="ii-label">{{ field.label }}</span>
                <span class="ii-value certification-text">{{ field.value }}</span>
              </div>
            </div>

            <div v-if="certificationImageGroups.length" class="image-section">
              <div v-for="group in certificationImageGroups" :key="group.label" class="image-group">
                <div class="cb-label">{{ group.label }}</div>
                <div class="image-grid">
                  <button
                    v-for="media in group.items"
                    :key="media.url"
                    class="image-link"
                    type="button"
                    @click="openMediaPreview(media)"
                  >
                    <img
                      v-if="media.type === 'image'"
                      :src="media.url"
                      :alt="group.label"
                      referrerpolicy="no-referrer"
                    >
                    <div v-else class="file-card">
                      <div class="file-card__icon">文件</div>
                      <div class="file-card__name">{{ media.name }}</div>
                      <div class="file-card__action">点击下载</div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="dialog-empty">暂未提交认证资料</div>
        </div>

        <div v-if="isReviewMode" class="form-group">
          <label>驳回原因</label>
          <textarea
            v-model.trim="reviewReason"
            rows="4"
            placeholder="审核驳回时请填写原因"
          ></textarea>
        </div>
      </div>
      <div v-else class="dialog-empty">未找到用户信息</div>

      <div slot="footer">
        <el-button @click="userDialogVisible = false">{{ isReviewMode ? '取消' : '关闭' }}</el-button>
        <el-button
          v-if="isReviewMode && canReviewCurrentUser"
          type="danger"
          :loading="submitting"
          @click="submitReview('rejected')"
        >
          驳回
        </el-button>
        <el-button
          v-if="isReviewMode && canReviewCurrentUser"
          type="success"
          :loading="submitting"
          @click="submitReview('approved')"
        >
          通过
        </el-button>
      </div>
    </el-dialog>

    <el-dialog :title="previewTitle" :visible.sync="previewDialogVisible" width="960px" top="6vh">
      <div v-if="previewUrl" class="media-preview">
        <img
          v-if="previewType === 'image'"
          class="media-preview__image"
          :src="previewUrl"
          :alt="previewTitle"
          referrerpolicy="no-referrer"
        >
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  adminUsersList,
  adminUserDetail,
  adminUserCertificationStatus
} from '../../api/userMG'
import { downloadRemoteFile } from '../../utils/download'

export default {
  name: 'system-user',
  data() {
    return {
      loading: false,
      dialogLoading: false,
      submitting: false,
      list: [],
      filters: { name: '', type: '' },
      draftFilters: { name: '', type: '' },
      pagination: {
        currentPage: 1,
        pageSize: 15,
        total: 0,
        lastPage: 1
      },
      userDialogVisible: false,
      dialogMode: 'detail',
      currentUser: null,
      reviewReason: '',
      previewDialogVisible: false,
      previewTitle: '附件预览',
      previewType: 'image',
      previewUrl: ''
    }
  },
  computed: {
    isReviewMode() {
      return this.dialogMode === 'review'
    },
    dialogTitle() {
      if (!this.currentUser) {
        return this.isReviewMode ? '认证审核' : '用户详情'
      }

      return this.isReviewMode ? `认证审核 - ${this.currentUser.name}` : `用户详情 - ${this.currentUser.name}`
    },
    canReviewCurrentUser() {
      return !!(this.currentUser && this.currentUser.certificationStatus === 'pending')
    },
    userInfoFields() {
      if (!this.currentUser) return []

      const fields = [
        { label: '用户ID', value: this.currentUser.id },
        { label: '用户类型', value: this.currentUser.typeText },
        { label: '姓名/企业名', value: this.currentUser.name },
        { label: '手机号', value: this.currentUser.phone },
        { label: '注册时间', value: this.currentUser.createdAt },
        { label: '认证状态', value: this.currentUser.certificationStatusText }
      ]

      if (this.currentUser.type === 'mcn') {
        fields.splice(4, 0, {
          label: '统一社会信用代码',
          value: this.currentUser.companyCode || '—'
        })
      }

      if (this.currentUser.thirdPlatform) {
        fields.push({ label: '第三方平台', value: this.currentUser.thirdPlatform })
      }

      if (this.currentUser.thirdAuthStatus) {
        fields.push({ label: '第三方授权状态', value: this.currentUser.thirdAuthStatus })
      }

      return fields
    },
    certificationInfoFields() {
      if (!this.currentUser || !this.currentUser.certification) return []

      const certification = this.currentUser.certification
      const fields = [
        { label: '审核状态', value: certification.statusText },
        { label: '提交时间', value: certification.submittedAt || '—' },
        { label: '审核时间', value: certification.reviewedAt || '—' }
      ]

      if (this.currentUser.type === 'individual') {
        fields.splice(1, 0, {
          label: '身份证号',
          value: certification.idCardNo || '—'
        })
      }

      if (this.currentUser.type === 'mcn') {
        fields.push(
          { label: '法人姓名', value: certification.legalPersonName || '—' },
          { label: '注册资本', value: certification.registeredCapital || '—' },
          {
            label: '所在地区',
            value: this.joinRegion(certification.province, certification.city),
            wide: true
          },
          {
            label: '详细地址',
            value: certification.addressDetail || '—',
            wide: true
          }
        )
      }

      if (certification.rejectReason) {
        fields.push({
          label: '驳回原因',
          value: certification.rejectReason,
          wide: true
        })
      }

      return fields
    },
    certificationImageGroups() {
      if (!this.currentUser || !this.currentUser.certification) return []

      const certification = this.currentUser.certification
      const groups = []

      if (this.currentUser.type === 'individual') {
        this.pushImageGroup(groups, '身份证正面', certification.idCardFront)
        this.pushImageGroup(groups, '身份证反面', certification.idCardBack)
      }

      if (this.currentUser.type === 'mcn') {
        this.pushImageGroup(groups, '营业执照', certification.businessLicense)
        this.pushImageGroup(groups, '法人身份证正面', certification.legalPersonIdCardFront)
        this.pushImageGroup(groups, '法人身份证反面', certification.legalPersonIdCardBack)
        this.pushImageGroup(groups, '主播授权协议', certification.authorizationAgreements)
      }

      return groups
    }
  },
  created() {
    this.loadList(1)
  },
  methods: {
    async loadList(page) {
      this.loading = true

      const targetPage = page || this.pagination.currentPage
      const params = {
        page: targetPage,
        per_page: this.pagination.pageSize
      }

      if (this.filters.name) {
        params.name = this.filters.name
      }

      if (this.filters.type) {
        params.type = this.filters.type
      }

      try {
        const res = await adminUsersList(params)

        if (!this.isListSuccess(res)) {
          this.list = []
          this.pagination.total = 0
          this.$message.error(this.getErrorMessage(res, '用户列表加载失败'))
          return
        }

        const meta = res.meta || {}

        this.list = Array.isArray(res.data) ? res.data.map(item => this.normalizeUser(item)) : []
        this.pagination.currentPage = Number(meta.current_page) || targetPage
        this.pagination.pageSize = Number(meta.per_page) || this.pagination.pageSize
        this.pagination.total = Number(meta.total) || 0
        this.pagination.lastPage = Number(meta.last_page) || 1
      } catch (error) {
        this.list = []
        this.pagination.total = 0
        this.$message.error(this.getErrorMessage(error, '用户列表加载失败'))
      } finally {
        this.loading = false
      }
    },
    normalizeUser(item) {
      const certification = this.normalizeCertification(item.certification, item.type, item.is_certified)
      const certificationStatus = certification ? certification.status : (item.is_certified ? 'approved' : 'none')

      return {
        id: item.id,
        name: item.name || '—',
        phone: item.phone || '—',
        type: item.type || '',
        typeText: item.type_text || this.typeText(item.type),
        companyCode: item.company_code || '',
        isCertified: !!item.is_certified,
        thirdPlatform: item.third_platform || '',
        thirdAuthStatus: item.third_auth_status || '',
        createdAt: item.created_at || '—',
        certification,
        certificationStatus,
        certificationStatusText: certification ? certification.statusText : this.certificationStatusText(certificationStatus)
      }
    },
    normalizeCertification(certification, fallbackType, isCertified) {
      if (!certification || typeof certification !== 'object') {
        return null
      }

      const status = certification.status || (isCertified ? 'approved' : 'none')

      return {
        id: certification.id,
        type: certification.type || fallbackType,
        status,
        statusText: certification.status_text || this.certificationStatusText(status),
        idCardNo: certification.id_card_no || null,
        idCardFront: certification.id_card_front || '',
        idCardBack: certification.id_card_back || '',
        businessLicense: certification.business_license || '',
        legalPersonName: certification.legal_person_name || '',
        province: certification.province || '',
        city: certification.city || '',
        addressDetail: certification.address_detail || '',
        registeredCapital: certification.registered_capital || '',
        legalPersonIdCardFront: certification.legal_person_id_card_front || '',
        legalPersonIdCardBack: certification.legal_person_id_card_back || '',
        authorizationAgreements: Array.isArray(certification.authorization_agreements)
          ? certification.authorization_agreements.filter(Boolean)
          : [],
        rejectReason: certification.reject_reason || '',
        submittedAt: certification.submitted_at || null,
        reviewedAt: certification.reviewed_at || null
      }
    },
    extractDetail(res) {
      if (!res || typeof res !== 'object' || res.message || res.detail || res.errors) {
        return null
      }

      if (res.data && !Array.isArray(res.data) && typeof res.data === 'object') {
        return res.data
      }

      return res
    },
    isListSuccess(res) {
      return !!(res && typeof res === 'object' && Array.isArray(res.data) && res.meta && !res.message && !res.detail && !res.errors)
    },
    isRequestFailed(res) {
      return typeof res === 'string' || !!(res && typeof res === 'object' && (res.message || res.detail || res.errors))
    },
    typeText(type) {
      return {
        individual: '个人',
        mcn: 'MCN机构'
      }[type] || type || '—'
    },
    certificationStatusText(status) {
      return {
        pending: '待审核',
        approved: '已通过',
        rejected: '已驳回',
        none: '未提交'
      }[status] || status || '未提交'
    },
    statusClass(status) {
      return {
        status: true,
        'status-pending': status === 'pending',
        'status-approved': status === 'approved',
        'status-rejected': status === 'rejected',
        'status-none': !status || status === 'none'
      }
    },
    canReview(item) {
      return !!(item && item.certificationStatus === 'pending')
    },
    joinRegion(province, city) {
      const list = [province, city].filter(Boolean)
      return list.length ? list.join(' / ') : '—'
    },
    pushImageGroup(groups, label, source) {
      const urls = Array.isArray(source) ? source.filter(Boolean) : (source ? [source] : [])
      const items = urls.map(url => ({
        url,
        name: this.extractFileName(url),
        type: this.isImageFile(url) ? 'image' : 'file'
      }))

      if (items.length) {
        groups.push({ label, items })
      }
    },
    isImageFile(url) {
      if (!url || typeof url !== 'string') return false

      const normalizedUrl = url.split('?')[0].split('#')[0]
      return /\.(png|jpg|jpeg|gif|webp|bmp)$/i.test(normalizedUrl)
    },
    extractFileName(url) {
      if (!url || typeof url !== 'string') return '附件'

      const normalizedUrl = url.split('?')[0].split('#')[0]
      const segments = normalizedUrl.split('/')
      return segments[segments.length - 1] || '附件'
    },
    openMediaPreview(media) {
      if (!media || !media.url) return

      if (media.type !== 'image') {
        return downloadRemoteFile(media.url, {
          fileName: media.name
        })
      }

      this.previewType = media.type
      this.previewUrl = media.url
      this.previewTitle = '预览'
      this.previewDialogVisible = true
    },
    getErrorMessage(error, fallback) {
      if (!error) return fallback
      if (typeof error === 'string') return error
      if (error.message) return error.message
      if (error.detail) return error.detail
      if (error.errors) {
        const firstKey = Object.keys(error.errors)[0]
        const firstError = firstKey ? error.errors[firstKey] : ''

        if (Array.isArray(firstError)) {
          return firstError[0] || fallback
        }

        return firstError || fallback
      }

      return fallback
    },
    applySearch() {
      this.filters = Object.assign({}, this.draftFilters)
      this.pagination.currentPage = 1
      this.loadList(1)
    },
    handlePageChange(page) {
      this.loadList(page)
    },
    async openUserDialog(item, mode) {
      this.dialogMode = mode
      this.userDialogVisible = true
      this.dialogLoading = true
      this.currentUser = null
      this.reviewReason = ''

      try {
        const res = await adminUserDetail(item.id)
        const detail = this.extractDetail(res)

        if (!detail || !detail.id) {
          this.$message.error(this.getErrorMessage(res, '用户详情加载失败'))
          this.userDialogVisible = false
          return
        }

        this.currentUser = this.normalizeUser(detail)

        if (this.isReviewMode && !this.canReviewCurrentUser) {
          this.$message.warning('该用户当前不是待审核状态')
        }
      } catch (error) {
        this.$message.error(this.getErrorMessage(error, '用户详情加载失败'))
        this.userDialogVisible = false
      } finally {
        this.dialogLoading = false
      }
    },
    async submitReview(status) {
      if (!this.currentUser) return

      const rejectReason = this.reviewReason.trim()

      if (status === 'rejected' && !rejectReason) {
        this.$message.error('请填写驳回原因')
        return
      }

      const confirmText = status === 'approved' ? '确认通过该用户认证吗？' : '确认驳回该用户认证吗？'

      try {
        await this.$confirm(confirmText, '提示', { type: 'warning' })
      } catch (error) {
        return
      }

      this.submitting = true

      try {
        const res = await adminUserCertificationStatus(this.currentUser.id, {
          status,
          reject_reason: status === 'rejected' ? rejectReason : ''
        })

        if (this.isRequestFailed(res)) {
          this.$message.error(this.getErrorMessage(res, '认证审核处理失败'))
          return
        }

        this.$message.success(status === 'approved' ? '认证已通过' : '认证已驳回')
        this.userDialogVisible = false
        await this.loadList(this.pagination.currentPage)
      } catch (error) {
        this.$message.error(this.getErrorMessage(error, '认证审核处理失败'))
      } finally {
        this.submitting = false
      }
    }
  }
}
</script>

<style scoped>
.user-page {
  gap: 18px;
}

.user-page__topbar {
  min-height: 70px;
  margin: -20px -20px 0;
  padding: 18px 24px 20px;
  background: #e9eef5;
}

.user-page__title h1 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  line-height: 1.2;
}

.user-page__title p {
  margin-top: 10px;
  font-size: 13px;
  color: #334155;
}

.title-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  font-size: 18px;
}

.user-list-card {
  padding: 22px 22px 20px;
  border-radius: 16px;
}

.user-search-bar {
  align-items: center;
  margin-bottom: 14px;
}

.user-search-bar input {
  min-width: 240px;
}

.user-table-wrap {
  overflow-x: auto;
}

.extra-info {
  line-height: 1.6;
  color: var(--cog-text-secondary);
}

.table-empty {
  padding: 24px 12px;
  text-align: center;
  color: var(--cog-text-secondary);
}

.user-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.dialog-state,
.dialog-empty {
  padding: 32px 16px;
  text-align: center;
  color: var(--cog-text-secondary);
}

.certification-block {
  margin-bottom: 0;
}

.certification-text {
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-all;
}

.image-section {
  margin-top: 20px;
}

.image-group + .image-group {
  margin-top: 16px;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
}

.image-link {
  display: block;
  overflow: hidden;
  width: 100%;
  padding: 0;
  border: 1px solid var(--cog-border-light);
  border-radius: var(--cog-radius-sm);
  background: #fff;
  cursor: pointer;
}

.image-link img {
  display: block;
  width: 100%;
  height: 160px;
  object-fit: cover;
}

.file-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  height: 160px;
  padding: 16px;
  background: #f8fafc;
  text-align: center;
}

.file-card__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: #fee2e2;
  color: #dc2626;
  font-size: 14px;
  font-weight: 700;
}

.file-card__name {
  color: var(--cog-text-primary);
  font-size: 13px;
  font-weight: 600;
}

.file-card__action {
  color: var(--cog-text-secondary);
  font-size: 12px;
}

.media-preview {
  min-height: 420px;
}

.media-preview__image {
  display: block;
  max-width: 100%;
  max-height: 78vh;
  margin: 0 auto;
}


.form-group {
  margin-top: 16px;
}

.form-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 13px;
  font-weight: 600;
}

.form-group textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1.5px solid var(--cog-border);
  border-radius: var(--cog-radius-sm);
  font-size: 13px;
  resize: vertical;
}

.full-width {
  grid-column: 1 / -1;
}

.status-none {
  background: #f3f4f6;
  color: #6b7280;
}

@media (max-width: 768px) {
  .user-page__topbar {
    margin: -16px -16px 0;
    padding: 16px;
  }

  .user-search-bar input,
  .user-search-bar select,
  .user-search-bar button {
    width: 100%;
  }

  .user-pagination {
    justify-content: center;
  }

  .image-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>



// WEBPACK FOOTER //
// src/views/system/user.vue